import React, { useState, useEffect } from 'react';
import {
  // useIsFetchingAccountData, // Unused variable
  useDailyProfits,
  useIsFetchingDailyProfits,
  usePositionStatistics,
  useFundPoolStats,
  useFetchFundPoolStats,
  useIsFetchingFundPool,
  useAppStore,
} from '../stores/useAppStore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import GlassCard from './ui/GlassCard';
import NeonText from './ui/NeonText';
import AnimatedCounter from './ui/AnimatedCounter';
import GlowButton from './ui/GlowButton';
import AnimatedTitle from './ui/AnimatedTitle';
import LazyLoad from './ui/LazyLoad';
import StaggeredAnimation from './ui/StaggeredAnimation';
import { ProfitTimeRange, QuantAccountTab } from '@/services/enums';

interface QuantAccountProps {
  loading?: boolean;
}

const QuantAccount: React.FC<QuantAccountProps> = ({ loading = false }) => {
  // 从store获取账户数据
  // const isFetchingAccountData = useIsFetchingAccountData(); // Unused variable

  // 从store获取每日收益数据
  const dailyProfits = useDailyProfits();
  const isFetchingDailyProfits = useIsFetchingDailyProfits();

  const fundPoolStats = useFundPoolStats();
  const fetchFundPoolStats = useFetchFundPoolStats();
  const isFetchingFundPool = useIsFetchingFundPool();

  // 从store获取持仓统计数据
  const positionStatistics = usePositionStatistics();
  const [activeTab, setActiveTab] = useState<QuantAccountTab>('overview');
  const [timeRange, setTimeRange] = useState<ProfitTimeRange>('all');
  const [selectedMonth, setSelectedMonth] = useState<string>(
    new Date().toISOString().slice(0, 7), // YYYY-MM format
  );

  // 获取 fetchAccountData 方法
  const fetchAccountData = useAppStore((state) => state.fetchAccountData);

  // 当时间范围改变时，重新获取统计数据
  useEffect(() => {
    fetchAccountData(timeRange);
  }, [timeRange, fetchAccountData]);

  // 计算盈亏数据 - 使用持仓统计数据和每日收益数据
  const calculatePnLData = () => {
    // 使用统计数据
    const totalPnL = positionStatistics?.totalPnL || 0;
    const winRate = positionStatistics?.winRate || 0;
    const totalTrades = positionStatistics?.totalTrades || 0;
    const todayPnL = positionStatistics?.todayPnL || 0;

    // 使用每日收益数据生成图表记录
    let records: any[] = [];
    if (dailyProfits && dailyProfits.length > 0) {
      const now = new Date();
      let filteredRecords = dailyProfits;

      // 根据时间范围过滤数据
      if (timeRange !== 'all') {
        const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
        const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        filteredRecords = dailyProfits.filter((record: any) => new Date(record.date) >= cutoffDate);
      }

      // 转换数据格式以兼容现有的图表组件
      records = filteredRecords.map((record: any) => ({
        uTime: new Date(record.date).getTime().toString(),
        realizedPnl: record.cumulativeProfit, // 改为累计收益
        date: record.date,
        cumulativeProfit: record.cumulativeProfit,
      }));
    }

    return {
      totalPnL,
      todayPnL,
      winRate,
      totalTrades,
      records,
    };
  };

  // 获取日历数据 - 使用每日收益数据
  const getCalendarData = () => {
    const dataToUse = dailyProfits;

    if (!dataToUse || dataToUse.length === 0) {
      return {};
    }

    const [year, month] = selectedMonth.split('-').map(Number);

    // 创建日历数据 - 直接使用字符串匹配，避免日期解析问题
    const calendarData: { [key: string]: number } = {};

    // 遍历所有数据，匹配当月的数据
    dataToUse.forEach((record: any) => {
      // record.date 应该是字符串格式 YYYY-MM-DD
      if (typeof record.date === 'string' && record.date.startsWith(selectedMonth)) {
        // 直接使用 date 作为键，确保格式一致
        calendarData[record.date] = parseFloat(record.dailyProfit || 0);
      } else if (record.date instanceof Date || typeof record.date === 'string') {
        // 如果是 Date 对象或日期字符串，转换为 YYYY-MM-DD 格式
        const recordDate = new Date(record.date);
        // 检查是否是当月的日期
        if (recordDate.getFullYear() === year && recordDate.getMonth() === month - 1) {
          const recordYear = recordDate.getFullYear();
          const recordMonth = String(recordDate.getMonth() + 1).padStart(2, '0');
          const recordDay = String(recordDate.getDate()).padStart(2, '0');
          const dateKey = `${recordYear}-${recordMonth}-${recordDay}`;
          calendarData[dateKey] = parseFloat(record.dailyProfit || 0);
        }
      }
    });

    return calendarData;
  };

  const pnlData = calculatePnLData();
  const calendarData = getCalendarData();
  const currentMonthKey = new Date().toISOString().slice(0, 7);

  const monthlyProfit =
    dailyProfits?.reduce((sum: number, record: any) => {
      const dateStr = typeof record.date === 'string' ? record.date : new Date(record.date).toISOString().slice(0, 10);
      if (dateStr.startsWith(currentMonthKey)) {
        return sum + Number(record.dailyProfit || 0);
      }
      return sum;
    }, 0) || 0;

  const bestDayProfit =
    dailyProfits && dailyProfits.length > 0
      ? dailyProfits.reduce((max: number, record: any) => {
          const value = Number(record.dailyProfit || 0);
          return Math.max(max, value);
        }, Number.NEGATIVE_INFINITY)
      : 0;

  const normalizedBestDay = bestDayProfit === Number.NEGATIVE_INFINITY ? 0 : bestDayProfit;

  const averageDailyProfit =
    dailyProfits && dailyProfits.length > 0
      ? dailyProfits.reduce((sum: number, record: any) => sum + Number(record.dailyProfit || 0), 0) /
        dailyProfits.length
      : 0;

  const losingDays = dailyProfits?.filter((record: any) => Number(record.dailyProfit || 0) < 0).length || 0;

  const investedAmount = parseFloat(fundPoolStats?.totalInvestedAmount || '0');
  const totalPnL = parseFloat(fundPoolStats?.totalPnL || '0') || 0;
  const globalTotalAssets = investedAmount + totalPnL;
  const globalProfitRate = investedAmount > 0 ? (totalPnL / investedAmount) * 100 : 0;

  useEffect(() => {
    if (!fundPoolStats && !isFetchingFundPool) {
      fetchFundPoolStats();
    }
  }, [fundPoolStats, isFetchingFundPool, fetchFundPoolStats]);

  const accountOverviewItems = [
    {
      label: '总资产',
      value: globalTotalAssets,
      prefix: '$',
      color: 'text-blue-300',
      bg: 'bg-blue-500/10',
      decimals: 2,
    },
    {
      label: '总收益率',
      value: globalProfitRate,
      prefix: globalProfitRate >= 0 ? '+' : '-',
      suffix: '%',
      color: globalProfitRate >= 0 ? 'text-green-300' : 'text-pink-300',
      bg: globalProfitRate >= 0 ? 'bg-green-500/10' : 'bg-pink-500/10',
      decimals: 2,
    },
    {
      label: '当月收益',
      value: monthlyProfit,
      prefix: monthlyProfit >= 0 ? '+$' : '-$',
      color: monthlyProfit >= 0 ? 'text-green-400' : 'text-pink-400',
      bg: monthlyProfit >= 0 ? 'bg-green-500/15' : 'bg-pink-500/15',
      decimals: 2,
    },
    {
      label: '最佳单日收益',
      value: normalizedBestDay,
      prefix: normalizedBestDay >= 0 ? '+$' : '-$',
      color: normalizedBestDay >= 0 ? 'text-green-400' : 'text-pink-400',
      bg: normalizedBestDay >= 0 ? 'bg-green-500/15' : 'bg-pink-500/15',
      decimals: 2,
    },
    {
      label: '平均日收益',
      value: averageDailyProfit,
      prefix: averageDailyProfit >= 0 ? '+$' : '-$',
      color: averageDailyProfit >= 0 ? 'text-cyan-300' : 'text-pink-300',
      bg: 'bg-cyan-500/10',
      decimals: 2,
    },
    {
      label: '亏损天数',
      value: losingDays,
      prefix: '',
      suffix: '天',
      color: 'text-amber-300',
      bg: 'bg-amber-500/10',
      decimals: 0,
    },
  ];

  // 合并加载状态
  // 只在初始加载时显示全屏加载，切换时间范围时不显示全屏加载
  const isLoading = loading || isFetchingDailyProfits;

  // 生成日历网格
  const generateCalendarGrid = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0); // 当月的最后一天
    const startDate = new Date(firstDay);
    // 从周日开始，但需要调整时区
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    startDate.setHours(0, 0, 0, 0); // 确保时间归零

    const days: any[] = [];
    const currentDate = new Date(startDate);

    // 计算需要生成的周数：确保覆盖到当月最后一天
    // 从 startDate 到 lastDay 需要的天数
    const daysDiff = Math.ceil((lastDay.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const weeksNeeded = Math.ceil((daysDiff + 1) / 7); // +1 包含起始日，除以7向上取整
    const totalDays = weeksNeeded * 7; // 确保是7的倍数

    // 生成足够天数的日历，确保包含当月最后一天
    for (let i = 0; i < totalDays; i++) {
      // 使用本地日期格式，避免时区问题
      const year = currentDate.getFullYear();
      const monthStr = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${monthStr}-${day}`;
      const isCurrentMonth = currentDate.getMonth() === month - 1;
      const isToday = dateStr === new Date().toISOString().slice(0, 10);
      const pnl = calendarData[dateStr] || 0;

      days.push(
        <div
          key={dateStr}
          className={`
            aspect-square flex flex-col items-center justify-center text-xs rounded-lg border border-white/10
            ${isCurrentMonth ? 'text-white' : 'text-white/40'}
            ${isToday ? 'bg-cyan-400/20 border-cyan-400/50' : ''}
            ${pnl > 0 ? 'bg-green-400/10 border-green-400/30' : pnl < 0 ? 'bg-pink-400/10 border-pink-400/30' : ''}
            hover:bg-white/5 transition-colors duration-200
          `}
        >
          <span className="font-semibold">{currentDate.getDate()}</span>
          {isCurrentMonth && calendarData[dateStr] !== undefined && pnl !== 0 && (
            <span className={`text-xs font-medium ${pnl > 0 ? 'text-green-400' : 'text-pink-400'}`}>
              {pnl > 0 ? '+' : ''}
              {pnl.toFixed(0)}
            </span>
          )}
        </div>,
      );

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  if (isLoading) {
    return (
      <GlassCard className="p-6 mb-6" glow>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="p-6 mb-6 overflow-visible" glow>
      <StaggeredAnimation delay={200} animation="fadeInUp">
        <AnimatedTitle variant="gradient" size="xl" className="mb-6">
          量化账户
        </AnimatedTitle>

        {/* 标签页 */}
        <div className="flex mb-6 bg-white/10 rounded-lg p-1">
          <GlowButton
            onClick={() => setActiveTab('overview')}
            className={`flex-1 ${activeTab === 'overview' ? '' : 'opacity-50'}`}
            variant={activeTab === 'overview' ? 'primary' : 'secondary'}
            size="sm"
          >
            盈亏总览
          </GlowButton>
          <GlowButton
            onClick={() => setActiveTab('calendar')}
            className={`flex-1 ${activeTab === 'calendar' ? '' : 'opacity-50'}`}
            variant={activeTab === 'calendar' ? 'primary' : 'secondary'}
            size="sm"
          >
            盈亏日历
          </GlowButton>
        </div>
      </StaggeredAnimation>

      {activeTab === 'overview' && (
        <div>
          {/* 时间范围选择 */}
          <div className="flex gap-2 mb-6">
            {(['7d', '30d', '90d', 'all'] as const).map((range, index) => (
              <div
                key={range}
                className="transition-all duration-600 ease-out opacity-0 translate-y-8"
                style={{
                  transitionDelay: `${index * 100}ms`,
                  animation: `fadeInUp 0.6s ease-out ${index * 100}ms forwards`,
                }}
              >
                <GlowButton
                  onClick={() => setTimeRange(range)}
                  variant={timeRange === range ? 'primary' : 'secondary'}
                  size="sm"
                  className={timeRange === range ? '' : 'opacity-60'}
                >
                  {range === '7d' ? '近7天' : range === '30d' ? '近30天' : range === '90d' ? '近90天' : '全部'}
                </GlowButton>
              </div>
            ))}
          </div>

          {/* 盈亏统计 */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {[
              {
                label: '总盈亏',
                value: pnlData.totalPnL,
                color: pnlData.totalPnL >= 0 ? 'green' : 'pink',
                prefix: pnlData.totalPnL >= 0 ? '+$' : '-$',
              },
              {
                label: '今日盈亏',
                value: pnlData.todayPnL,
                color: pnlData.todayPnL >= 0 ? 'green' : 'pink',
                prefix: pnlData.todayPnL >= 0 ? '+$' : '-$',
              },
              {
                label: '胜率',
                value: pnlData.winRate,
                color: 'cyan',
                suffix: '%',
                decimals: 1,
              },
              { label: '总仓位数', value: pnlData.totalTrades, color: 'blue', decimals: 0 },
            ].map((stat, index) => (
              <div
                key={stat.label}
                className="transition-all duration-600 ease-out opacity-0 translate-y-8"
                style={{
                  transitionDelay: `${150 + index * 100}ms`,
                  animation: `fadeInUp 0.6s ease-out ${150 + index * 100}ms forwards`,
                }}
              >
                <GlassCard className="p-4">
                  <p className="text-sm text-white/60 mb-2">{stat.label}</p>
                  <NeonText className="text-2xl" color={stat.color as any}>
                    <AnimatedCounter
                      value={Math.abs(stat.value)}
                      prefix={stat.prefix}
                      suffix={stat.suffix}
                      decimals={stat.decimals}
                    />
                  </NeonText>
                </GlassCard>
              </div>
            ))}
          </div>

          {/* 累计收益 */}
          <div
            className="transition-all duration-600 ease-out opacity-0 translate-y-8"
            style={{
              transitionDelay: '200ms',
              animation: 'fadeInUp 0.6s ease-out 200ms forwards',
            }}
          >
            <GlassCard className="p-4 overflow-visible">
              <NeonText className="text-lg mb-4" color="cyan">
                累计收益
              </NeonText>
              <LazyLoad
                height={160}
                placeholder={
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  </div>
                }
              >
                <div className="w-full mt-3" style={{ height: '160px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={(() => {
                        // 根据选择的时间范围获取数据
                        const now = new Date();
                        let filteredRecords = pnlData.records;

                        // 根据时间范围过滤数据
                        if (timeRange !== 'all') {
                          const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
                          const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
                          filteredRecords = pnlData.records.filter((record: any) => {
                            const recordDate = new Date(parseInt(record.uTime));
                            return recordDate >= cutoffDate;
                          });
                        }

                        const records = filteredRecords
                          .sort((a: any, b: any) => parseInt(a.uTime) - parseInt(b.uTime))
                          .map((record: any) => ({
                            date: new Date(parseInt(record.uTime)).toLocaleDateString('zh-CN', {
                              month: 'short',
                              day: 'numeric',
                            }),
                            value: parseFloat(record.realizedPnl || 0),
                            fullDate: record.date,
                          }));

                        console.log('📊 图表数据:', records, '时间范围:', timeRange);
                        return records;
                      })()}
                      margin={{ top: 5, right: 5, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="2 2" stroke="rgba(0, 245, 255, 0.2)" strokeWidth={1} />
                      <XAxis
                        dataKey="date"
                        stroke="#00f5ff"
                        strokeWidth={2}
                        fontSize={12}
                        tick={{ fill: '#00f5ff', fontSize: 12 }}
                        axisLine={{
                          stroke: '#00f5ff',
                          strokeWidth: 2,
                        }}
                        tickLine={{
                          stroke: '#00f5ff',
                          strokeWidth: 1,
                        }}
                        domain={['dataMin', 'dataMax']}
                      />
                      <YAxis
                        stroke="#00f5ff"
                        strokeWidth={2}
                        fontSize={12}
                        tick={{ fill: '#00f5ff', fontSize: 12 }}
                        tickFormatter={(value) => value.toFixed(0)}
                        axisLine={{
                          stroke: '#00f5ff',
                          strokeWidth: 2,
                        }}
                        tickLine={{
                          stroke: '#00f5ff',
                          strokeWidth: 1,
                        }}
                        domain={['dataMin', 'dataMax']}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(0,0,0,0.9)',
                          border: '1px solid rgba(0,245,255,0.3)',
                          borderRadius: '12px',
                          color: 'white',
                          backdropFilter: 'blur(10px)',
                        }}
                        labelStyle={{ color: 'rgba(0,245,255,0.9)' }}
                        formatter={(value: any, _name: any) => [`$${Number(value).toFixed(2)}`, '收益']}
                        labelFormatter={(label: any) => `日期: ${label}`}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={(() => {
                          const records = pnlData.records;
                          if (records.length === 0) return '#00f5ff';
                          const lastRecord = records[records.length - 1];
                          const lastValue = parseFloat(lastRecord.realizedPnl || 0);
                          return lastValue >= 0 ? '#00ff88' : '#ff0088';
                        })()}
                        strokeWidth={3}
                        dot={{ fill: '#00f5ff', strokeWidth: 2, r: 4 }}
                        activeDot={{
                          r: 6,
                          stroke: '#00f5ff',
                          strokeWidth: 2,
                          fill: '#00f5ff',
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </LazyLoad>

              {/* 图例 - 移到外层 */}
              <div className="flex justify-between items-center mt-2 text-xs">
                <span className="text-telegram-hint">
                  {timeRange === '7d'
                    ? '最近7天'
                    : timeRange === '30d'
                      ? '最近30天'
                      : timeRange === '90d'
                        ? '最近90天'
                        : '全部时间'}
                </span>
                <span
                  className={`px-2 py-1 rounded ${
                    pnlData.totalPnL >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  {pnlData.totalPnL >= 0 ? '+' : ''}
                  {pnlData.totalPnL.toFixed(2)}
                </span>
              </div>
            </GlassCard>
          </div>
        </div>
      )}

      {activeTab === 'calendar' && (
        <div>
          <div
            className="transition-all duration-600 ease-out opacity-0 translate-y-8"
            style={{
              transitionDelay: '200ms',
              animation: 'fadeInUp 0.6s ease-out 200ms forwards',
            }}
          >
            {/* 月份选择 */}
            <div className="flex items-center gap-4 mb-6">
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-white/10 text-white px-4 py-2 rounded-lg border border-white/20 backdrop-blur-sm focus:outline-none focus:border-cyan-400"
              />
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-400/30 rounded-full border border-green-400"></div>
                  <span className="text-white/80">盈利</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-pink-400/30 rounded-full border border-pink-400"></div>
                  <span className="text-white/80">亏损</span>
                </div>
              </div>
            </div>

            {/* 日历网格 */}
            <GlassCard className="p-4">
              <LazyLoad
                placeholder={
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  </div>
                }
              >
                <div className="grid grid-cols-7 gap-1 mb-3">
                  {['日', '一', '二', '三', '四', '五', '六'].map((day) => (
                    <div key={day} className="text-center text-sm text-white/70 font-semibold">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">{generateCalendarGrid()}</div>
              </LazyLoad>
            </GlassCard>
          </div>
        </div>
      )}
      {/* 账户概览 */}
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 via-purple-500/5 to-blue-500/5 p-4 mt-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-white/90 font-semibold">
            <span className="text-xl">📊</span>
            <span>账户概览</span>
          </div>
          <span className="text-xs text-white/60">实时更新</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {accountOverviewItems.map((item) => (
            <div key={item.label} className={`${item.bg} border border-white/10 rounded-xl p-3`}>
              <p className="text-xs text-white/60 mb-1">{item.label}</p>
              <NeonText className={`text-lg ${item.color}`} color="cyan">
                <AnimatedCounter
                  value={Math.abs(item.value)}
                  prefix={item.prefix}
                  suffix={item.suffix}
                  decimals={item.decimals}
                />
              </NeonText>
            </div>
          ))}
        </div>
      </div>
    </GlassCard>
  );
};

export default QuantAccount;
